import { useEffect, useRef, useState } from 'react';

/**
 * The client half of §1.8 — one hook behind both SSE channels.
 *
 * A news site's realtime layer is judged by how it behaves when the connection is *bad*,
 * not when it is good, so four of §1.8's requirements are the whole design here:
 *
 * - **Exponential backoff.** A dropped stream is retried after 1s, 2s, 4s… capped at 30s.
 *   A fixed one-second retry turns one reader on a flaky train into a request every
 *   second against a single shared-hosting process.
 * - **`Last-Event-ID` resumption.** Every event carries the outbox id it was written
 *   with; reconnecting asks to resume from the last one seen, so what happened during the
 *   gap arrives rather than being silently skipped. `EventSource` cannot set headers on a
 *   first connection, which is why the server also accepts `?lastEventId=`.
 * - **Polling fallback after three failed reconnects.** Some networks block
 *   `text/event-stream` outright; after three failures the hook stops trying and tells
 *   the caller to poll instead. A reader on such a network gets a 30-second-old ticker
 *   rather than a permanently frozen one.
 * - **A status the UI can show.** `connecting`, `open`, `reconnecting`, `polling` —
 *   because a reader watching a live blog deserves to know the page has stopped
 *   listening.
 *
 * The hook holds no event state of its own. It hands each payload to `onEvent`, which is
 * kept in a ref so a caller can pass an inline closure without tearing the stream down on
 * every render — reconnecting the socket because a parent re-rendered would be a subtle
 * and expensive bug.
 */
export type StreamStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'polling';

export interface StreamEvent {
  /** The outbox id (§1.8), monotonic across both channels. */
  id: number;
  data: unknown;
}

const MAX_RECONNECTS = 3;
const BASE_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 30_000;
/** §1.8: "clients must degrade to 30-second polling if the stream drops." */
export const POLL_INTERVAL_MS = 30_000;

/**
 * What to do after `failures` consecutive failures — the reconnection policy, pulled out
 * of the effect so it can be read and tested on its own rather than inferred from a
 * sequence of timers.
 */
export function retryPlan(failures: number): { action: 'retry' | 'poll'; delay: number } {
  if (failures > MAX_RECONNECTS) return { action: 'poll', delay: POLL_INTERVAL_MS };
  return {
    action: 'retry',
    delay: Math.min(BASE_BACKOFF_MS * 2 ** (failures - 1), MAX_BACKOFF_MS),
  };
}

/**
 * The URL to reconnect with. Resumption rides in the query string because `EventSource`
 * cannot set a header on the connection it opens itself; the server reads either form
 * (§1.8). Zero means "this client has seen nothing", and asking to resume from zero
 * would replay the whole outbox into a ticker that should simply start from now.
 */
export function resumeUrl(url: string, lastEventId: number): string {
  if (lastEventId <= 0) return url;
  return `${url}${url.includes('?') ? '&' : '?'}lastEventId=${lastEventId}`;
}

export function useEventStream({
  url,
  enabled = true,
  onEvent,
}: {
  /** The channel to open, without the resumption parameter — the hook adds that. */
  url: string;
  enabled?: boolean;
  onEvent: (event: StreamEvent) => void;
}): { status: StreamStatus } {
  const [status, setStatus] = useState<StreamStatus>('idle');
  const lastEventId = useRef(0);
  const handler = useRef(onEvent);
  handler.current = onEvent;

  useEffect(() => {
    if (!enabled || typeof EventSource === 'undefined') {
      setStatus('idle');
      return undefined;
    }

    let source: EventSource | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let failures = 0;
    let stopped = false;

    const connect = (): void => {
      if (stopped) return;
      setStatus(failures === 0 ? 'connecting' : 'reconnecting');

      source = new EventSource(resumeUrl(url, lastEventId.current));

      source.onopen = () => {
        failures = 0;
        setStatus('open');
      };

      source.onmessage = (message: MessageEvent<string>) => {
        const id = Number(message.lastEventId);
        if (Number.isFinite(id) && id > 0) lastEventId.current = id;
        try {
          handler.current({ id, data: JSON.parse(message.data) });
        } catch {
          // A malformed frame is one lost event, not a reason to drop the connection.
        }
      };

      source.onerror = () => {
        // `EventSource` retries by itself, on its own schedule and without resumption.
        // Closing first is what puts the backoff and the `Last-Event-ID` in our hands.
        source?.close();
        source = null;
        if (stopped) return;

        failures += 1;
        const plan = retryPlan(failures);
        if (plan.action === 'poll') {
          // Polling is the caller's job from here. The stream is still retried, quietly
          // and at the polling interval, because a reader whose train left the tunnel
          // should get their live connection back without reloading — but the status
          // stays `polling` until one of those attempts actually opens, so the
          // indicator does not flicker between two states the reader cannot act on.
          setStatus('polling');
          retryTimer = setTimeout(connectQuietly, plan.delay);
          return;
        }

        setStatus('reconnecting');
        retryTimer = setTimeout(connect, plan.delay);
      };
    };

    /** A retry from the polling state: same connection, no change of status if it fails. */
    const connectQuietly = (): void => {
      failures = MAX_RECONNECTS;
      connect();
      setStatus('polling');
    };

    connect();

    return () => {
      stopped = true;
      clearTimeout(retryTimer);
      source?.close();
      setStatus('idle');
    };
  }, [url, enabled]);

  return { status };
}
